import React from "react";
import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { getAnnoMode } from "@/utils/getAnnoMode";
import { StyledLayoutWrapper, StyledContentGrid } from "./styled";
import {
  useTestCases,
  useTemplateSelection,
  useFilterScopes,
  useTenantConfig,
  useViewControls,
  usePipelineData,
  useSelectionDetails,
  useGeneratedFilterSelections,
} from "./hooks";
import {
  ControlsSidebar,
  GeneratedFilterSidebar,
  SummaryColumn,
  TemplatesColumn,
  ConnectionsView,
} from "./components";

export const FiltersPlayground = () => {
  const platformConfigs = useGetQueryData("platformConfigs");
  const allTplsUnfiltered = useGetQueryData("allTpls") || [];
  const smTagTypesConfig = platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.annotation_tagTypesConfig || null;
  const hasNewTopicModelling = getAnnoMode(platformConfigs) !== "legacy";
  const isSplitMode = getAnnoMode(platformConfigs) === "split";

  const { activeCaseId, setActiveCaseId, activeCase, testCases } = useTestCases();
  const {
    selectedTpls,
    templateOptions,
    selectedTemplateOptions,
    handleTemplateToggle,
    customTemplateInput,
    handleCustomTemplateInputChange,
    handleAddCustomTemplate,
    customTemplateFeedback,
    generatedContentTypes,
    allTemplates,
    usePlatformTemplates,
    handleToggleTemplateSource,
  } = useTemplateSelection(activeCase);

  const { filterScopes, scopeOptionList, selectedScopeOptions, handleScopeToggle } =
    useFilterScopes(activeCase);

  const {
    selfManaged,
    setSelfManaged,
    annotationCounts,
    handleAnnotationCountChange,
    selfManagedOption,
  } = useTenantConfig(activeCase);

  const {
    unifiedView,
    setUnifiedView,
    unifiedOption,
    debugMode,
    setDebugMode,
    forceV3,
    setForceV3,
    forceV3Option,
    showConnections,
    setShowConnections,
    connectionsOption,
  } = useViewControls(activeCase);
  const {
    pipelineOutput,
    finalSections,
    annotationSection,
    documentSection,
    unifiedFilters,
    totalFilters,
    hasGeneratedFilters,
    stageData,
    overviewData,
    overviewConfig,
  } = usePipelineData({
    selectedTpls,
    allTemplates: allTplsUnfiltered,
    filterScopes,
    selfManaged,
    annotationCounts,
    unifiedView,
    smTagTypesConfig,
    hasNewTopicModelling,
    isSplitMode,
  });
  const { generatedActiveFilters, setGeneratedActiveFilters } =
    useGeneratedFilterSelections(pipelineOutput);
  const selectionDetails = useSelectionDetails({
    selectedTpls,
    filterScopes,
    annotationCounts,
  });

  return (
    <SidebarLayoutV2
      lsbWidth={400}
      lsbComp={
        <GeneratedFilterSidebar
          hasGeneratedFilters={hasGeneratedFilters}
          finalSections={finalSections}
          generatedActiveFilters={generatedActiveFilters}
          setGeneratedActiveFilters={setGeneratedActiveFilters}
          generatedContentTypes={generatedContentTypes}
          debugMode={debugMode}
          forceV3={forceV3}
        />
      }
      lsbAllowResize
      lsbAllowCollapse
      rsbWidth={380}
      rsbAllowCollapse
      toggleState={{lsb: true, rsb: false}}
      
      rsbComp={
        <ControlsSidebar
          activeCaseId={activeCaseId}
          setActiveCaseId={setActiveCaseId}
          testCases={testCases}
          activeCase={activeCase}
          scopeOptionList={scopeOptionList}
          selectedScopeOptions={selectedScopeOptions}
          handleScopeToggle={handleScopeToggle}
          annotationCounts={annotationCounts}
          handleAnnotationCountChange={handleAnnotationCountChange}
          selfManaged={selfManaged}
          setSelfManaged={setSelfManaged}
          selfManagedOption={selfManagedOption}
          unifiedView={unifiedView}
          setUnifiedView={setUnifiedView}
          unifiedOption={unifiedOption}
          debugMode={debugMode}
          setDebugMode={setDebugMode}
          forceV3={forceV3}
          setForceV3={setForceV3}
          forceV3Option={forceV3Option}
          showConnections={showConnections}
          setShowConnections={setShowConnections}
          connectionsOption={connectionsOption}
        />
      }
    >
      <StyledLayoutWrapper>
        {showConnections ? (
          <ConnectionsView allTpls={allTplsUnfiltered} />
        ) : (
        <StyledContentGrid>
          <SummaryColumn
            overviewData={overviewData}
            overviewConfig={overviewConfig}
            selectionDetails={selectionDetails}
            stageData={stageData}
          />

          <TemplatesColumn
            templateOptions={templateOptions}
            selectedTemplateOptions={selectedTemplateOptions}
            handleTemplateToggle={handleTemplateToggle}
            customTemplateInput={customTemplateInput}
            handleCustomTemplateInputChange={handleCustomTemplateInputChange}
            handleAddCustomTemplate={handleAddCustomTemplate}
            customTemplateFeedback={customTemplateFeedback}
            annotationSection={annotationSection}
            documentSection={documentSection}
            unifiedView={unifiedView}
            unifiedFilters={unifiedFilters}
            totalFilters={totalFilters}
            generatedActiveFilters={generatedActiveFilters}
            usePlatformTemplates={usePlatformTemplates}
            handleToggleTemplateSource={handleToggleTemplateSource}
            selfManaged={selfManaged}
            setSelfManaged={setSelfManaged}
            selfManagedOption={selfManagedOption}
            forceV3={forceV3}
            setForceV3={setForceV3}
            forceV3Option={forceV3Option}
            showConnections={showConnections}
            setShowConnections={setShowConnections}
            connectionsOption={connectionsOption}
          />
        </StyledContentGrid>
        )}
      </StyledLayoutWrapper>
    </SidebarLayoutV2>
  );
};
